import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.836.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/></svg>
);

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(email, password); toast.success("Welcome back ♡"); navigate(next); }
    catch (err) { toast.error(err.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="login-page" className="min-h-screen flex items-center justify-center py-16 px-6 relative overflow-hidden">
      <div className="hero-orb" style={{ width: 600, height: 600, top: -200, right: -160, opacity: .5 }}/>
      <div className="surface p-10 w-full max-w-md relative z-[2]">
        <span className="eyebrow">Sign in</span>
        <h1 className="text-[2.4rem] mt-3 mb-1">Welcome back ♡</h1>
        <p className="text-[var(--ink-soft)] text-sm">Pick up where you left off.</p>

        <form onSubmit={submit} className="mt-7 space-y-4">
          <Input testid="login-email" label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}/>
          <Input testid="login-password" label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}/>
          <button type="submit" disabled={busy} data-testid="login-submit" className="btn btn-primary w-full justify-center">
            {busy ? "..." : "Sign in"}
          </button>
        </form>

        <div className="my-5 flex items-center gap-3">
          <div className="flex-1 h-px bg-[#fde0e8]"/>
          <span className="text-xs uppercase tracking-[0.18em] text-[var(--ink-soft)]">or</span>
          <div className="flex-1 h-px bg-[#fde0e8]"/>
        </div>

        <button onClick={googleLogin} data-testid="google-login-button"
          className="w-full bg-white border border-[#fde0e8] hover:border-[var(--bloom)] py-3.5 rounded-full font-bold uppercase tracking-[0.14em] text-sm flex items-center justify-center gap-3 transition">
          <GoogleIcon/> Continue with Google
        </button>

        <p className="mt-6 text-sm text-center text-[var(--ink-soft)]">
          New here? <Link to="/register" className="font-bold" style={{color:"var(--bloom)"}} data-testid="go-register-link">Create an account</Link>
        </p>
      </div>
    </div>
  );
}

function Input({ testid, label, ...props }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.16em] text-[var(--ink-soft)] font-bold">{label}</span>
      <input data-testid={testid} required {...props}
        className="mt-1.5 w-full bg-white border border-[#fde0e8] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--bloom)]"/>
    </label>
  );
}
